import React, { useMemo } from 'react';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import PendingOrders from '../../screens/orders/PendingOrders';
import CompletedOrders from '../../screens/orders/CompletedOrders';
import PartialOrders from '../../screens/orders/PartialOrders';
import { useAppSelector } from '../../redux/helper';
import { SettingsDetails } from '../../redux/SettingsSlice';
import { Dimensions, View } from 'react-native';
import { fs } from '../../utils/libs';
import { GTFonts } from '../../theme';
import Filter from '../../components/Reusables/Filter';
import { isEmployeeAdminRole } from '../../utils/roles';
const Tab = createMaterialTopTabNavigator();

type OrdersScreenTabNavigatorProps = {
  dateFilterVisible?: boolean;
  dateFilterActive?: boolean;
  dateFilterStartISO?: string;
  dateFilterEndISO?: string;
  onApplyDateFilter?: (fromISO: string, toISO: string) => void;
};

const OrdersScreenTabNavigator: React.FC<OrdersScreenTabNavigatorProps> = ({
  dateFilterVisible = false,
  dateFilterActive = false,
  dateFilterStartISO,
  dateFilterEndISO,
  onApplyDateFilter,
}) => {
  const settings = useAppSelector(SettingsDetails);
  const employeeRole = useAppSelector(
    s => s.auth.inApp.account.employeeInfo?.bp_employee_role,
  );
  const minimumOrdersStartDate = useMemo(() => {
    if (isEmployeeAdminRole(employeeRole)) return undefined;
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - 30);
    minDate.setHours(0, 0, 0, 0);
    return minDate;
  }, [employeeRole]);

  // Memoize tab configuration to prevent re-renders and ensure static structure
  const tabConfiguration = useMemo(() => {
    const baseScreenOptions = {
      tabBarLabelStyle: {
        fontSize: fs(15),
        lineHeight: fs(18),
        fontFamily: GTFonts.regular,
      },
      tabBarIndicatorStyle: { backgroundColor: '#1560bd' },
      tabBarActiveTintColor: '#1560bd',
      tabBarBounces: true,
      tabBarPressOpacity: 1,
      lazy: true,
      lazyPreloadDistance: 1,
      initialLayout: { width: Dimensions.get('window').width },
      swipeVelocityImpact: 0.2,
      springConfig: {
        damping: 20,
        stiffness: 100,
        restSpeedThreshold: 0.001,
        restDisplacementThreshold: 0.001,
      },
    };

    return {
      screenOptions: baseScreenOptions,
      showPartialOrders: settings?.allow_partial_orders !== false, // Default to true when loading
    };
  }, [settings?.allow_partial_orders]);

  return (
    <View style={{ flex: 1 }}>
      {dateFilterVisible && (
        <Filter
          title="Filter Orders By Date"
          mode="daily"
          initialStartISO={dateFilterStartISO}
          initialEndISO={dateFilterEndISO}
          minimumStartDate={minimumOrdersStartDate}
          onApply={(from, to) => onApplyDateFilter?.(from, to)}
        />
      )}
      <Tab.Navigator
        initialRouteName="Completed"
        screenOptions={tabConfiguration.screenOptions}>
        <Tab.Screen
          name="Completed"
          options={{ title: 'Completed', animationEnabled: true }}>
          {props => (
            <CompletedOrders
              {...props}
              dateFilterActive={dateFilterActive}
              dateFilterStartISO={dateFilterStartISO}
              dateFilterEndISO={dateFilterEndISO}
            />
          )}
        </Tab.Screen>
        <Tab.Screen
          name="Pending"
          options={{ title: 'Pending', animationEnabled: true }}>
          {props => (
            <PendingOrders
              {...props}
              dateFilterActive={dateFilterActive}
              dateFilterStartISO={dateFilterStartISO}
              dateFilterEndISO={dateFilterEndISO}
            />
          )}
        </Tab.Screen>
        {tabConfiguration.showPartialOrders && (
          <Tab.Screen
            name="Partially Paid"
            options={{ title: 'Partially Paid', animationEnabled: true }}>
            {props => (
              <PartialOrders
                {...props}
                dateFilterActive={dateFilterActive}
                dateFilterStartISO={dateFilterStartISO}
                dateFilterEndISO={dateFilterEndISO}
              />
            )}
          </Tab.Screen>
        )}
      </Tab.Navigator>
    </View>
  );
};

export default OrdersScreenTabNavigator;
